// The real engine always calculates internally in US customary units (feet,
// cfs) regardless of the project's own `[OPTIONS] FLOW_UNITS` — every raw
// `.inp` value must be converted to feet/cfs ONCE at read time (`toFeet`/
// `toCfs` etc. below) and back only at report time. Skipping this for a
// non-CFS project silently corrupts every downstream calculation: a real
// production model (Victoria, `FLOW_UNITS CMS`) had pipe diameters like 0.2
// meters read as 0.2 FEET — a pipe ~3.3x too small in every linear
// dimension — before this conversion existed.

import { findOption } from "./inp/options.ts";

export type UnitSystemName = "US" | "SI";

/** Branded at rest (a struct field holding an already-converted value) so a raw un-converted `.inp` value assigned straight into an internal-feet/cfs slot is a compile error, not a silent scale bug — does NOT catch two different conversions producing the same branded type with the wrong factor; only per-function unit tests catch that. */
export type Feet = number & { readonly __unit: "Feet" };
export type Cfs = number & { readonly __unit: "Cfs" };

/**
 * Every valid `[OPTIONS] FLOW_UNITS` token — the one place the full 6-token
 * set is spelled out. `readFlowUnits`'s own validation below and `FLOW_QCF`'s
 * keys further down (plus `OptionsDialog.tsx`'s `<select>` options) all
 * reference `FLOW_UNIT_TOKENS` instead of re-declaring the set, so what used
 * to be 3-4 hand-copied lists across two files can't drift apart.
 */
export const FLOW_UNIT_TOKENS = ["CFS", "GPM", "MGD", "CMS", "LPS", "MLD"] as const;
/** Not exported — only `FLOW_QCF` below needs it, to tie its keys to `FLOW_UNIT_TOKENS` at the type level. */
type FlowUnitToken = (typeof FLOW_UNIT_TOKENS)[number];

const US_FLOW_UNIT_WORDS = new Set(["CFS", "GPM", "MGD"]);

export function readFlowUnits(raw: ReadonlyMap<string, string[][]>): string {
  const value = (findOption(raw, "FLOW_UNITS") ?? "CFS").toUpperCase();
  return (FLOW_UNIT_TOKENS as readonly string[]).includes(value) ? value : "CFS";
}

export function unitSystemOf(flowUnits: string): UnitSystemName {
  return US_FLOW_UNIT_WORDS.has(flowUnits) ? "US" : "SI";
}

const METERS_PER_FOOT = 0.3048;

/** A real geographic (haversine) distance, always in meters, converted to the project's own length unit — feet for a US flow-unit project, meters (1:1) for an SI one. Same US/SI split `drawnConduitLength` (conduitLength.ts) and MeasurePanel.tsx's distance tool both need, kept in one place. */
export function metersToProjectLength(meters: number, flowUnits: string): number {
  return unitSystemOf(flowUnits) === "US" ? meters / METERS_PER_FOOT : meters;
}

// {US, SI} pairs — a raw value divided by the entry for the project's own
// unit system gives the internal US value.
const LENGTH_UCF: Record<UnitSystemName, number> = { US: 1.0, SI: 0.3048 };
/** Acres (US) or hectares (SI) -> ft² — `[SUBCATCHMENTS]` Area only, NOT the same factor as an area expressed directly in length units squared (see `toSquareFeet`). */
const LANDAREA_UCF: Record<UnitSystemName, number> = { US: 2.2956e-5, SI: 0.92903e-5 };
const RAINFALL_UCF: Record<UnitSystemName, number> = { US: 43200.0, SI: 1097280.0 };
const EVAPRATE_UCF: Record<UnitSystemName, number> = { US: 1036800.0, SI: 26334720.0 };
const RAINDEPTH_UCF: Record<UnitSystemName, number> = { US: 12.0, SI: 304.8 };
/** A PER-DEGREE ratio only — a raw temperature VALUE (not an offset/coefficient) needs the real `9/5*C+32` affine conversion instead, which a pure ratio can't express. */
const TEMPERATURE_UCF: Record<UnitSystemName, number> = { US: 1.0, SI: 1.8 };
const WINDSPEED_UCF: Record<UnitSystemName, number> = { US: 1.0, SI: 1.608 };

export function toFeet(value: number, sys: UnitSystemName): Feet {
  return (value / LENGTH_UCF[sys]) as Feet;
}

/** Governs how every link's own `.inp` offset column(s) are read — see `resolveLinkOffsetFt` for what each convention means. */
type LinkOffsetConvention = "DEPTH" | "ELEVATION";

export function readLinkOffsetConvention(
  raw: ReadonlyMap<string, string[][]>
): LinkOffsetConvention {
  return (findOption(raw, "LINK_OFFSETS") ?? "").trim().toUpperCase() === "ELEVATION"
    ? "ELEVATION"
    : "DEPTH";
}

/** A raw `*` token (ELEVATION mode only) means that end's elevation isn't specified — must resolve to `0` (same as no offset under DEPTH), not `Number("*")`'s `NaN`, which would silently poison every downstream calculation for that end. */
export function isLinkOffsetStar(
  token: string | undefined,
  convention: LinkOffsetConvention
): boolean {
  return convention === "ELEVATION" && (token ?? "").trim() === "*";
}

/**
 * Under DEPTH, the raw offset already IS the depth above the node invert —
 * returned unchanged (a genuinely negative DEPTH-mode CONDUIT offset is
 * used as given, unclamped).
 *
 * Under ELEVATION, the raw value is an ABSOLUTE elevation instead — reading
 * it as a depth-above-invert directly double-counts the node's own invert
 * (real bug: fabricated a ~16.8ft phantom flow depth on a real fixture,
 * driving the momentum equation to produce grossly incorrect flow). Must
 * subtract the node invert here instead, floored at 0.
 */
export function resolveLinkOffsetFt(
  rawOffsetFt: number,
  nodeInvertFt: number,
  convention: LinkOffsetConvention
): number {
  if (convention === "DEPTH") return rawOffsetFt;
  return Math.max(0, rawOffsetFt - nodeInvertFt);
}

/**
 * The inverse of `toFeet`. Needed at FORMULA-EVALUATION time, not just
 * data-read time: weir/outlet FUNCTIONAL flow and storage's area-vs-depth
 * relation are non-linear in a length term (`pow(head, 1.5)`), and their
 * coefficient is calibrated for the project's raw unit — a pure input-scale
 * conversion does NOT commute with a non-linear power, so it must be
 * reconverted immediately before every such `pow()` call, not only once at
 * read time.
 */
export function fromFeet(value: Feet, sys: UnitSystemName): number {
  return value * LENGTH_UCF[sys];
}

export function toSquareFeetLandArea(value: number, sys: UnitSystemName): number {
  return value / LANDAREA_UCF[sys];
}

/** Needed at FORMULA-EVALUATION time — buildup AREA-normalized coefficients (e.g. lb/acre/day) are calibrated against acres/hectares, not ft², same "raw scale, re-derived every time a coefficient is evaluated" reasoning as `fromFeet`. */
export function fromSquareFeetLandArea(value: number, sys: UnitSystemName): number {
  return value * LANDAREA_UCF[sys];
}

export function toSquareFeet(value: number, sys: UnitSystemName): number {
  const f = LENGTH_UCF[sys];
  return value / (f * f);
}

export function fromSquareFeet(value: number, sys: UnitSystemName): number {
  const f = LENGTH_UCF[sys];
  return value * f * f;
}

export function toCubicFeet(value: number, sys: UnitSystemName): number {
  const f = LENGTH_UCF[sys];
  return value / (f * f * f);
}

export function fromCubicFeet(value: number, sys: UnitSystemName): number {
  const f = LENGTH_UCF[sys];
  return value * f * f * f;
}

export function toFeetPerSecRainfall(value: number, sys: UnitSystemName): number {
  return value / RAINFALL_UCF[sys];
}

/** Needed at FORMULA-EVALUATION time, not just report time: EXPON_WASHOFF evaluates a non-linear power of a rate expressed in the project's raw unit, not internal ft/sec — same non-commuting-power reasoning as `fromFeet`. */
export function fromFeetPerSecRainfall(value: number, sys: UnitSystemName): number {
  return value * RAINFALL_UCF[sys];
}

/** Genuinely a DIFFERENT conversion than `toFeetPerSecRainfall` alone (an extra TEMPERATURE factor up front) — this value is calibrated per degree, not a plain rate. */
export function toFeetPerSecMeltCoeff(value: number, sys: UnitSystemName): number {
  return toFeetPerSecRainfall(value * TEMPERATURE_UCF[sys], sys);
}

/** Deliberately the plain ratio, NOT the affine `9/5*C+32` conversion a raw temperature value needs — an offset has no zero-point of its own to shift. */
export function toFahrenheitOffset(value: number, sys: UnitSystemName): number {
  return value * TEMPERATURE_UCF[sys];
}

export function toMph(value: number, sys: UnitSystemName): number {
  return value / WINDSPEED_UCF[sys];
}

export function toFeetRainDepth(value: number, sys: UnitSystemName): Feet {
  return (value / RAINDEPTH_UCF[sys]) as Feet;
}

export function fromFeetRainDepth(value: Feet, sys: UnitSystemName): number {
  return value * RAINDEPTH_UCF[sys];
}

export function toFeetPerSecEvap(value: number, sys: UnitSystemName): number {
  return value / EVAPRATE_UCF[sys];
}

/** A genuinely DIFFERENT time basis than `fromFeetPerSecRainfall` — evaporation reports per-DAY, precipitation/infiltration per-HOUR. */
export function fromFeetPerSecEvap(value: number, sys: UnitSystemName): number {
  return value * EVAPRATE_UCF[sys];
}

/** Deliberately its own constant, not `RAINFALL_UCF` — a real, separate factor in the source, not a renamed duplicate. Depths must go through `fromFeet` first (same non-commuting-power reasoning), only the final result comes back through this. */
const GWFLOW_UCF: Record<UnitSystemName, number> = { US: 43560.0, SI: 3048.0 };
export function toFeetPerSecGwFlow(value: number, sys: UnitSystemName): number {
  return value / GWFLOW_UCF[sys];
}

// Indexed by the literal flow-unit token, not just US/SI — GPM/MGD/LPS/MLD
// each have their own distinct factor within their own unit system. Typed by
// FlowUnitToken (not plain `string`) so adding/removing a token in
// FLOW_UNIT_TOKENS without updating this table is a compile error rather
// than a silent `?? 1.0` fallback.
const FLOW_QCF: Record<FlowUnitToken, number> = {
  CFS: 1.0,
  GPM: 448.831,
  MGD: 0.64632,
  CMS: 0.02832,
  LPS: 28.317,
  MLD: 2.4466,
};

function qcfOf(flowUnits: string): number {
  return (FLOW_QCF as Record<string, number>)[flowUnits] ?? 1.0;
}

export function toCfs(value: number, flowUnits: string): Cfs {
  return (value / qcfOf(flowUnits)) as Cfs;
}

/** Must be applied at REPORT time to every routed flow, not left in raw internal cfs — a no-op for a CFS/US project, but wrong by the CMS factor (~0.02832) for a metric one. */
export function fromCfs(value: Cfs, flowUnits: string): number {
  return value * qcfOf(flowUnits);
}

/** A DIFFERENT, more precise constant than `FLOW_QCF.CMS` (0.02832) — the real weir/storage flow formula divides by this specific value, not the general CMS report-time factor; a ~0.01% mismatch if the two are conflated. */
const M3_PER_FT3 = 0.028317;

/**
 * A weir/storage formula's own SI-evaluated intermediate result (always
 * m³/s) back to internal cfs. Deliberately NOT `toCfs(value, flowUnits)`:
 * weir/storage area coefficients are calibrated against the LENGTH unit
 * system (meters vs feet), the same for CMS/LPS/MLD alike, not against
 * whichever of those three the project happens to choose for reporting
 * flow — so the conversion factor here is the fixed `M3_PER_FT3` constant,
 * never `flowUnits`-specific.
 */
export function fromCubicMetersPerSecToCfs(value: number, sys: UnitSystemName): Cfs {
  return (sys === "SI" ? value / M3_PER_FT3 : value) as Cfs;
}

/** lb (US) or kg (SI) PER mg — converts between a pollutant's own reporting mass unit (mg/µg) and the weight unit buildup mass/coefficients are always calibrated in, regardless of which reporting unit a given pollutant uses. */
const MASS_UCF: Record<UnitSystemName, number> = { US: 2.203e-6, SI: 1.0e-6 };

/** Count-based units (`#/L`, e.g. bacteria) have no real mass, so `mcf` is `1.0` — buildup mass IS the count. */
export function pollutantMcf(units: string, sys: UnitSystemName): number {
  const upper = units.toUpperCase();
  if (upper.startsWith("MG")) return MASS_UCF[sys];
  if (upper.startsWith("UG")) return MASS_UCF[sys] / 1000;
  return 1.0;
}

/** Washoff concentration is computed internally in mass/ft³, but a pollutant's own reporting unit is always mass/LITER regardless of `FLOW_UNITS` — a fixed physical constant, not a `.inp`-unit-system conversion. */
export const LPER_FT3 = 28.317;
